import { useMemo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ActivityTimeline,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableId,
  DescriptionItem,
  DescriptionList,
  EntityDrawer,
  MetricCard,
  PageState,
  ProgressBar,
  Skeleton,
  TabBar,
  deltaFromRates,
  splitMacros,
} from "@fox1ck-org/ui";
import { LineChart } from "@fox1ck-org/ui/charts/recharts";
import type { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";
import { ResourceKind } from "@gen/tracker/v1/common_pb";
import { ActionType, type Offer, type OfferNumbers } from "@gen/tracker/v1/offer_pb";
import { DimensionFilterOp } from "@gen/tracker/v1/reporting_pb";

import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";
import { formatStamp } from "@/lib/utils";
import { carryPeriod } from "@/lib/route-tab";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useCheckOfferLink, useOfferNumbers } from "@/lib/services/offer-service";
import { useReport } from "@/lib/services/report-service";
import { useCampaignsByIds } from "@/lib/services/campaign-service";
import { useSearchConversions } from "@/lib/services/conversion-service";
import { useSearchAuditEvents, actionLabel } from "@/lib/services/audit-service";
import { useNetworkStatuses } from "@/lib/services/network-service";
import { OFFER_STATUS_VIEW, OUR_STATUSES, OUR_STATUS_VIEW, capShare, linkMacros, modelCode, money, payoutLine } from "@/components/offers/labels";
import { LinkCheckLine } from "@/components/offers/link-check";

/**
 * The offer card (board OfferCard) — in the package's EntityDrawer over the
 * list (decision 08: an offer is read and changed beside its list, not on a
 * page of its own). Its tabs are addresses: /library/offers/:id[/campaigns|
 * conversions|history]. Every number is the report engine's for the Top
 * bar's period (ListOfferNumbers and GetBreakdownDrilldown filtered to the
 * offer); the card's own share of the general report is card У5's to embed.
 *
 * «При капе трафик идёт на …» is not drawn: the edge does not apply offer
 * caps (journal У6, blocked on the owner), so the line would describe a
 * redirect that does not happen.
 */

export const OFFER_TABS = ["summary", "campaigns", "conversions", "history"] as const;
export type OfferTab = (typeof OFFER_TABS)[number];

const TAB_LABEL: Record<OfferTab, TKey> = {
  summary: "u6.card.tab.summary",
  campaigns: "u6.card.tab.campaigns",
  conversions: "u6.card.tab.conversions",
  history: "u6.card.tab.history",
};

export interface OfferCardProps {
  offer: Offer | undefined;
  loading: boolean;
  error?: unknown;
  network?: AffiliateNetwork;
  tab: OfferTab;
  onTab: (tab: OfferTab) => void;
  onClose: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onArchive?: () => void;
}

export function OfferCard({ offer, loading, error, network, tab, onTab, onClose, onEdit, onDuplicate, onArchive }: OfferCardProps) {
  const t = useT();
  const p = useReportParams();
  const id = offer?.id ?? "";
  const numbers = useOfferNumbers({ ids: id ? [id] : [], range: { from: p.from, to: p.to }, timezone: p.tz, basis: p.basis, compare: p.compareRange });
  const row = numbers.data?.items[0];
  const locked = !!offer?.source;

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={offer?.name ?? t("u6.card.loading")}
      description={offer ? metaLine(offer, network, t) : undefined}
      width="2xl"
      primaryAction={offer && onEdit ? { label: t("u6.card.edit"), onClick: onEdit } : undefined}
      secondaryAction={offer && onDuplicate ? { label: t("u6.card.duplicate"), onClick: onDuplicate } : undefined}
      destructiveAction={offer && onArchive && !locked ? { label: t("u6.common.archive"), onClick: onArchive } : undefined}
    >
      {error ? (
        <PageState mode="error" title={t("u6.card.loadFailed")} error={error} />
      ) : loading || !offer ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={OFFER_STATUS_VIEW[offer.status].tone} dot>
              {t(OFFER_STATUS_VIEW[offer.status].label)}
            </Badge>
            {row?.cap && Number(row.cap.dailyLimit) > 0 ? (
              <Badge variant={row.cap.reached ? "warn" : "neutral"}>
                {t("u6.card.capBadge", { used: formatInt(Number(row.cap.dailyUsed)), limit: formatInt(Number(row.cap.dailyLimit)) })}
              </Badge>
            ) : null}
            {locked ? <Badge variant="info">{t("u6.card.sourced", { source: offer.source })}</Badge> : null}
            <div className="flex-1" />
            <CopyableId value={offer.id} label={t("u6.offer.id")} truncate="end" />
          </div>
          <TabBar
            aria-label={t("u6.card.tabs")}
            value={tab}
            onValueChange={(v) => onTab(v as OfferTab)}
            tabs={OFFER_TABS.map((k) => ({ value: k, label: t(TAB_LABEL[k]), count: k === "campaigns" && row ? row.campaignCount : undefined }))}
          />
          {tab === "summary" ? <Summary offer={offer} network={network} row={row} loading={numbers.isLoading} /> : null}
          {tab === "campaigns" ? <CampaignsTable offerId={offer.id} /> : null}
          {tab === "conversions" ? <Conversions offerId={offer.id} limit={50} /> : null}
          {tab === "history" ? <History resourceId={offer.id} kind={ResourceKind.OFFER} /> : null}
        </div>
      )}
    </EntityDrawer>
  );
}

function metaLine(o: Offer, n: AffiliateNetwork | undefined, t: ReturnType<typeof useT>): string {
  return [
    n ? t("u6.card.meta.partner", { name: n.name }) : null,
    payoutLine(o.payout) ?? `${modelCode(o.payout?.model ?? 0)} ${DASH}`,
    o.country.length ? t("u6.card.meta.countries", { list: o.country.join(", ") }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function Panel({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 py-3">
        <CardTitle className="text-section-title">{title}</CardTitle>
        {right}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-4">{children}</CardContent>
    </Card>
  );
}

const pct = (a: number | undefined, b: number | undefined, good: "up" | "down" = "up") =>
  a !== undefined && b !== undefined && b !== 0 ? deltaFromRates(a, b, { good }) : undefined;

function Summary({ offer, network, row, loading }: { offer: Offer; network?: AffiliateNetwork; row?: OfferNumbers; loading: boolean }) {
  const t = useT();
  const { search } = useLocation();
  const cur = statsRowToNumbers(row?.stats);
  const prev = row?.compare ? statsRowToNumbers(row.compare) : undefined;
  const kpi = (label: TKey, key: string, value: string, opts: { good?: "up" | "down"; tone?: "ok" | "danger"; comparison?: ReactNode } = {}) => (
    <MetricCard
      labelCase="none"
      key={key}
      label={t(label)}
      value={value}
      loading={loading}
      delta={prev ? pct(cur[key], prev[key], opts.good) : undefined}
      tone={opts.tone}
      comparison={opts.comparison}
    />
  );
  const profit = cur.profit;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-4 gap-2">
        {kpi("u6.kpi.clicks", "clicks", formatInt(cur.clicks))}
        {kpi("u6.kpi.leads", "leads", formatInt(cur.leads), { comparison: cur.cr !== undefined ? `CR ${formatPct(cur.cr)}` : undefined })}
        {kpi("u6.kpi.approved", "conversions", formatInt(cur.conversions), { comparison: cur.approve_rate !== undefined ? t("u6.kpi.approveShare", { pct: formatPct(cur.approve_rate, { digits: 0 }) }) : undefined })}
        {kpi("u6.kpi.hold", "conversions_hold", formatInt(cur.conversions_hold), { comparison: cur.revenue_hold ? t("u6.kpi.inTransit", { sum: formatMoney(cur.revenue_hold) }) : undefined })}
        {kpi("u6.kpi.revenue", "revenue", formatMoney(cur.revenue), { comparison: cur.epc !== undefined ? `EPC ${formatMoney(cur.epc, { unit: true })}` : undefined })}
        {kpi("u6.kpi.cost", "cost", formatMoney(cur.cost), { good: "down" })}
        {kpi("u6.kpi.profit", "profit", formatMoney(profit, { signed: true }), { tone: profit === undefined ? undefined : profit >= 0 ? "ok" : "danger" })}
        {kpi("u6.kpi.roi", "roi", formatPct(cur.roi, { digits: 0, signed: true }), { tone: cur.roi === undefined ? undefined : cur.roi >= 0 ? "ok" : "danger" })}
      </div>
      <WhereItLeads offer={offer} />
      <div className="grid grid-cols-2 gap-3">
        <Payout offer={offer} />
        <Caps row={row} />
      </div>
      <PartnerStatuses network={network} />
      <Usage offerId={offer.id} />
      <Trend offerId={offer.id} />
      <Panel
        title={t("u6.card.lastConversions")}
        right={
          <Link className="text-xs text-primary" to={`/journal/conversions${carryPeriod(search)}`}>
            {t("u6.common.all")} →
          </Link>
        }
      >
        <Conversions offerId={offer.id} limit={3} compact />
      </Panel>
    </div>
  );
}

/** «Куда ведёт»: the link with its macros marked, and the check on demand. */
function WhereItLeads({ offer }: { offer: Offer }) {
  const t = useT();
  const check = useCheckOfferLink();
  const url = offer.action?.payload ?? "";
  const parts = splitMacros(url, linkMacros(url).map((m) => `{${m}}`));
  return (
    <Panel title={t("u6.card.where")}>
      {url ? (
        <div className="break-all rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm leading-relaxed">
          {parts.map((part, i) =>
            part.macro ? (
              <span key={i} className="rounded-sm bg-primary/10 px-0.5 text-primary">
                {part.text}
              </span>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </div>
      ) : (
        <span className="text-muted-foreground">{t("u6.card.noLink")}</span>
      )}
      <div className="flex items-center gap-2">
        <LinkCheckLine result={check.data} pending={check.isPending} />
        <div className="flex-1" />
        <Button type="button" variant="secondary" size="sm" disabled={!url || check.isPending} onClick={() => check.mutate(url)}>
          {check.data ? t("u6.link.again") : t("u6.link.check")}
        </Button>
      </div>
      <DescriptionList>
        <DescriptionItem label={t("u6.card.how")} value={offer.action?.type === ActionType.LINK ? t("u6.ed.how.link") : t("u6.card.how.redirect")} />
        <DescriptionItem label={t("u6.card.macros")} value={<span className="font-mono">{linkMacros(url).join(", ") || DASH}</span>} />
      </DescriptionList>
    </Panel>
  );
}

function Payout({ offer }: { offer: Offer }) {
  const t = useT();
  const rules = offer.payout?.rules ?? [];
  const currency = offer.payout?.amount?.currency;
  return (
    <Panel title={t("u6.card.payout")} right={<span className="text-xs text-muted-foreground">{t("u6.card.rulesCount", { n: rules.length })}</span>}>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">{money(offer.payout?.amount?.amount, currency)}</span>
        <span className="text-xs text-muted-foreground">{modelCode(offer.payout?.model ?? 0)}</span>
      </div>
      {rules.length ? (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-3">
            <tr>
              <th className="py-1 text-left font-semibold">{t("u6.card.rules.country")}</th>
              <th className="py-1 text-left font-semibold">{t("u6.card.rules.model")}</th>
              <th className="py-1 text-right font-semibold">{t("u6.card.rules.amount")}</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="py-1.5">{r.country}</td>
                <td className="py-1.5">{modelCode(r.model)}</td>
                <td className="py-1.5 text-right tabular-nums">{money(r.amount?.amount, r.amount?.currency || currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Panel>
  );
}

function Caps({ row }: { row?: OfferNumbers }) {
  const t = useT();
  const cap = row?.cap;
  const share = capShare(cap?.dailyUsed, cap?.dailyLimit);
  return (
    <Panel title={t("u6.card.caps")}>
      {cap && share !== undefined ? (
        <>
          <div className="flex justify-between text-sm">
            <span>{t("u6.card.caps.today", { tz: cap.timezone })}</span>
            <span className="flex shrink-0 items-center gap-2 whitespace-nowrap tabular-nums">
              {cap.reached ? <Badge variant="warn">{t("u6.cap.reached")}</Badge> : null}
              {formatInt(Number(cap.dailyUsed))} / {formatInt(Number(cap.dailyLimit))}
            </span>
          </div>
          <ProgressBar value={Number(cap.dailyUsed)} max={Number(cap.dailyLimit)} tone={share >= 0.8 ? "warn" : "primary"} size="sm" showPercent={false} />
        </>
      ) : (
        <span className="text-sm text-muted-foreground">{t("u6.card.caps.none")}</span>
      )}
      <DescriptionList>
        <DescriptionItem
          label={t("u6.card.caps.total")}
          value={<span className="tabular-nums">{`${formatInt(Number(cap?.totalUsed ?? 0))} / ${Number(cap?.totalLimit ?? 0) > 0 ? formatInt(Number(cap?.totalLimit)) : t("u6.ed.noLimit")}`}</span>}
        />
        <DescriptionItem label={t("u6.card.caps.clicks")} value={Number(cap?.clickDailyLimit ?? 0) > 0 ? `${formatInt(Number(cap?.clickDailyUsed))} / ${formatInt(Number(cap?.clickDailyLimit))}` : t("u6.ed.noLimit")} />
        <DescriptionItem label={t("u6.card.caps.onCap")} value={<span className="whitespace-normal">{t("u6.card.caps.onCap.value")}</span>} />
      </DescriptionList>
    </Panel>
  );
}

/** «Статусы партнёрки»: the partner's words grouped by what they mean here. */
function PartnerStatuses({ network }: { network?: AffiliateNetwork }) {
  const t = useT();
  const words = useNetworkStatuses(network?.id);
  const groups = useMemo(() => {
    const by = new Map<number, string[]>();
    for (const w of words.data?.items ?? []) {
      if (!w.status) continue;
      by.set(w.status, [...(by.get(w.status) ?? []), w.word]);
    }
    return OUR_STATUSES.filter((s) => by.has(s)).map((s) => ({ status: s, words: by.get(s)! }));
  }, [words.data]);
  if (!network) return null;
  return (
    <Panel
      title={t("u6.card.statuses")}
      right={
        <Link className="text-xs text-primary" to={`/library/networks/${network.id}`}>
          {t("u6.card.statuses.fromPartner")}
        </Link>
      }
    >
      {words.isLoading ? <Skeleton className="h-16 w-full" /> : null}
      {groups.length === 0 && !words.isLoading ? <span className="text-sm text-muted-foreground">{t("u6.card.statuses.none")}</span> : null}
      {groups.map((g) => (
        <div key={g.status} className="flex items-center justify-between gap-3 border-t border-border pt-2 first:border-0 first:pt-0">
          <span className="min-w-0 truncate font-mono text-sm">{g.words.join(", ")}</span>
          <Badge variant={OUR_STATUS_VIEW[g.status as keyof typeof OUR_STATUS_VIEW].tone}>{t(OUR_STATUS_VIEW[g.status as keyof typeof OUR_STATUS_VIEW].label)}</Badge>
        </div>
      ))}
    </Panel>
  );
}

function useOfferReport(offerId: string, dims: string[]) {
  const p = useReportParams();
  return useReport({
    from: p.from,
    to: p.to,
    timezone: p.tz,
    basis: p.basis,
    dims,
    subtotals: false,
    filters: { dims: [{ key: "offer", op: DimensionFilterOp.IN, values: [offerId] }], metrics: [] },
    sort: dims[0] === "day" ? undefined : { key: "leads", desc: true },
  });
}

/** «Где используется»: the campaigns that sent people to the offer in the period. */
function Usage({ offerId }: { offerId: string }) {
  const t = useT();
  return (
    <Panel title={t("u6.card.usage")}>
      <CampaignRows offerId={offerId} limit={5} />
    </Panel>
  );
}

function CampaignsTable({ offerId }: { offerId: string }) {
  return <CampaignRows offerId={offerId} />;
}

function CampaignRows({ offerId, limit }: { offerId: string; limit?: number }) {
  const t = useT();
  const { search } = useLocation();
  const report = useOfferReport(offerId, ["campaign"]);
  const rows = (report.data?.rows ?? []).filter((r) => !r.spendWithoutClicks).slice(0, limit);
  const names = useCampaignsByIds(rows.map((r) => r.dimensionPath[0] ?? ""));
  if (report.isLoading) return <Skeleton className="h-20 w-full" />;
  if (rows.length === 0) return <span className="text-sm text-muted-foreground">{t("u6.card.usage.none")}</span>;
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase tracking-wide text-ink-3">
        <tr>
          <th className="py-1 text-left font-semibold">{t("u6.card.usage.campaign")}</th>
          <th className="py-1 text-right font-semibold">{t("u6.kpi.clicks")}</th>
          <th className="py-1 text-right font-semibold">{t("u6.kpi.leads")}</th>
          <th className="py-1 text-right font-semibold">{t("u6.kpi.profit")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const id = r.dimensionPath[0] ?? "";
          const n = statsRowToNumbers(r.stats);
          const name = names.data?.get(id)?.campaign?.name ?? id;
          return (
            <tr key={id} className="border-t border-border">
              <td className="max-w-0 truncate py-1.5">
                <Link className="hover:text-primary" to={`/campaigns/${id}${carryPeriod(search)}`}>
                  {name}
                </Link>
              </td>
              <td className="py-1.5 text-right tabular-nums">{formatInt(n.clicks)}</td>
              <td className="py-1.5 text-right tabular-nums">{formatInt(n.leads)}</td>
              <td className={`py-1.5 text-right tabular-nums ${n.profit === undefined ? "" : n.profit >= 0 ? "text-ok" : "text-destructive"}`}>{formatMoney(n.profit, { signed: true })}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** «Лиды и апрувы по дням» — the report by day, the same engine as everywhere. */
function Trend({ offerId }: { offerId: string }) {
  const t = useT();
  const report = useOfferReport(offerId, ["day"]);
  const data = useMemo(
    () =>
      (report.data?.rows ?? [])
        .map((r) => ({ day: r.dimensionPath[0] ?? "", n: statsRowToNumbers(r.stats) }))
        .sort((a, b) => a.day.localeCompare(b.day))
        .map((r) => ({ day: r.day.slice(8, 10), leads: r.n.leads ?? 0, approved: r.n.conversions ?? 0 })),
    [report.data],
  );
  return (
    <Panel title={t("u6.card.trend")}>
      {report.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : data.length === 0 ? (
        <span className="text-sm text-muted-foreground">{t("u6.card.trend.none")}</span>
      ) : (
        <LineChart
          data={data}
          xKey="day"
          variant="area"
          height={160}
          showLegend
          lines={[
            { key: "leads", label: t("u6.kpi.leads") },
            { key: "approved", label: t("u6.kpi.approved") },
          ]}
          valueFormatter={(v) => formatInt(v)}
        />
      )}
    </Panel>
  );
}

export function Conversions({ offerId, networkId, limit, compact }: { offerId?: string; networkId?: string; limit: number; compact?: boolean }) {
  const t = useT();
  const p = useReportParams();
  const q = useSearchConversions({ range: { from: p.from, to: p.to, timezone: p.tz }, offerId, networkId, limit });
  const items = q.data?.items ?? [];
  const campaigns = useCampaignsByIds(items.map((c) => c.campaignId));
  if (q.isLoading) return <Skeleton className="h-16 w-full" />;
  if (items.length === 0) return <span className="text-sm text-muted-foreground">{t("u6.card.conv.none")}</span>;
  return (
    <table className="w-full text-sm">
      {compact ? null : (
        <thead className="text-xs uppercase tracking-wide text-ink-3">
          <tr>
            <th className="py-1 text-left font-semibold">{t("u6.card.conv.at")}</th>
            <th className="py-1 text-left font-semibold">{t("u6.card.usage.campaign")}</th>
            <th className="py-1 text-left font-semibold">{t("u6.card.conv.status")}</th>
            <th className="py-1 text-right font-semibold">{t("u6.card.conv.payout")}</th>
          </tr>
        </thead>
      )}
      <tbody>
        {items.map((c) => {
          const view = OUR_STATUS_VIEW[c.status];
          return (
            <tr key={c.dedupKey} className="border-t border-border first:border-0">
              <td className="py-1.5 tabular-nums text-muted-foreground">{formatStamp(c.ts, p.tz)}</td>
              <td className="max-w-0 truncate py-1.5">{campaigns.data?.get(c.campaignId)?.campaign?.name ?? DASH}</td>
              <td className="py-1.5">
                <Badge variant={view.tone}>{t(view.label)}</Badge>
              </td>
              <td className="py-1.5 text-right tabular-nums">{money(c.payout?.amount, c.payout?.currency)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** «История»: the audit log of the object, newest first. */
export function History({ resourceId, kind }: { resourceId: string; kind: ResourceKind }) {
  const t = useT();
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 365 * 86400_000).toISOString().slice(0, 10);
  const q = useSearchAuditEvents({ range: { from, to }, resourceKind: kind, resourceId, limit: 100 });
  return (
    <ActivityTimeline
      isLoading={q.isLoading}
      emptyMessage={t("u6.card.history.none")}
      locale="ru-RU"
      events={(q.data?.items ?? []).map((e) => ({
        id: e.id,
        kind: e.action,
        at: e.ts,
        title: actionLabel(e.action),
        actorName: e.actorEmail || e.actorId,
      }))}
      labels={{ today: t("u6.common.today"), yesterday: t("u6.common.yesterday"), loadMore: t("u6.common.more"), loading: t("u6.common.loading") }}
    />
  );
}
